import { CustomEventWithDetail, html, on, rune, type Html, View } from 'rune-ts';
import klass from './HandmadeCreatorHeader.module.scss';
import {
  closeIcon,
  hamburgerIcon,
  HandmadeCreatorLogoPC,
  HandmadeCreatorLogoMO,
  shareIcon,
} from '../../icons';
import { typo, typoPcMo } from '../../../../../shared/typography/typo';
import { htmlIf } from '../../../../../shared/util';
import { MShopUtilF } from '../../../../../../../modules/MShop/Util/F/Function/module/MShopUtilF';
import UAParser from 'ua-parser-js';
import NewHeadroom from 'headroom.js';
import { pipe, throwIf, isNull } from '@fxts/core';
import motion_klass from '../MotionSection/MotionSection.module.scss';
import links_klass from '../LinksSection/LinksSection.module.scss';
import interview_klass from '../InterviewSection/InterviewSection.module.scss';
import question_klass from '../QuestionSection/QuestionSection.module.scss';

export class toggleHeaderMenu extends CustomEventWithDetail<{ status: boolean }> {}
export class navigateSection extends CustomEventWithDetail<{
  klass: string;
  is_link_section?: boolean;
  should_initialize_animation?: boolean;
}> {}

const navigation_position_list = [
  { klass: motion_klass.motion, is_link_section: false, should_initialize_animation: true },
  { klass: links_klass.links_wrapper, is_link_section: true, should_initialize_animation: false },
  { klass: interview_klass.interview, is_link_section: false, should_initialize_animation: false },
  { klass: question_klass.question, is_link_section: false, should_initialize_animation: false },
];

export class HandmadeCreatorHeader extends View {
  toggle_open = false;
  headroom?: NewHeadroom;

  constructor() {
    super({});
  }

  override template(): Html {
    const is_mobile = !!rune.getSharedData(this)?.is_mobile;

    if (is_mobile) {
      return html`
        <div class="${klass.header} ${typo('14_medium')}">
          <div class="${klass.logo}">
            <a href="/">
              <span class="${klass.handmade_logo}">
                ${is_mobile ? HandmadeCreatorLogoMO : HandmadeCreatorLogoPC}
              </span>
            </a>
          </div>
          <div class="${klass.icons}">
            <span class="${klass.share_btn} share-btn">${shareIcon}</span>
            <span class="${klass.hamburger_btn} hamburger_btn"
              >${this.toggle_open ? closeIcon : hamburgerIcon}</span
            >
          </div>
          ${htmlIf(
            html`
              <div class="${klass.hamburger_menus_wrapper}">
                <div class="${klass.hamburger_menus} ${typo('20_bold')}">
                  <div class="${klass.menu}" data-klass="${motion_klass.motion}">너 세상 이야기</div>
                  <div class="${klass.menu}" data-klass="${links_klass.links}">마플샵 소개</div>
                  <div class="${klass.menu}" data-klass="${interview_klass.interview}">크리에이터 이야기</div>
                  <div class="${klass.menu}" data-klass="${question_klass.question}">자주 묻는 질문</div>
                </div>
              </div>
            `,
            this.toggle_open,
          )}
        </div>
      `;
    } else {
      return html`
        <div class="${klass.header}">
          <div class="${klass.logo}">
            <a href="/@/handmade">
              <span class="${klass.handmade_logo}"> ${HandmadeCreatorLogoPC} </span>
            </a>
          </div>
          <div class="${klass.menus} ${typo('16_bold')}">
            <div class="${klass.menu}" data-klass="${motion_klass.motion}">너 세상 이야기</div>
            <div class="${klass.menu}" data-klass="${links_klass.links}">마플샵 소개</div>
            <div class="${klass.menu}" data-klass="${interview_klass.interview}">크리에이터 이야기</div>
            <div class="${klass.menu}" data-klass="${question_klass.question}">자주 묻는 질문</div>
            ${this._makeMenuButton({ content: '공유하기' })}
          </div>
        </div>
      `;
    }
  }

  protected override onRender() {
    this.headroom = new NewHeadroom(this.element(), {});
    this.headroom.init();

    this.delegate('click', `.${klass.menu}`, (e) => {
      this.closeMenu();
      const el = e.target as Element;
      const nav_items = el.parentNode ? Array.from(el.parentNode.children) : [];
      const idx = nav_items.indexOf(el);

      navigation_position_list[idx] &&
        this.dispatchEvent(navigateSection, {
          bubbles: true,
          detail: navigation_position_list[idx],
        });
    });
  }

  private _makeMenuButton({ content }: { content: string }) {
    const is_mobile = !!rune.getSharedData(this)?.is_mobile;

    return html` <div
      class="${klass.menu} ${klass.menu_button} share-btn  ${typoPcMo({
        is_mobile,
        pc: '16_bold',
        mo: '20_bold',
      })}"
    >
      ${content}
    </div>`;
  }

  @on('click', '.share-btn')
  onClickShareButton(e) {
    const is_app = MShopUtilF.isApp();
    const title = e.currentTarget.dataset.title;
    const ua = new UAParser();
    const app_version = (/MarppleShopApp@(\w+.\w+.\w+)/gm.exec(ua.getUA()) || [])[1];

    if (is_app && app_version && app_version >= '1.1.0') {
      return MShopUtilF.postMessage({
        share: {
          title: `${T('마플샵')} - ${title}`,
          url: location.href,
        },
      });
    }

    try {
      navigator.clipboard
        .writeText(location.href)
        .then(() => {
          MShopUtilF.popToastMsg(T('s_about::url_copied2'), 'info', { position: klass.toast_position });
        })
        .catch(() => {
          MShopUtilF.popToastMsg(T('s_about::url_failed'), 'info');
        });
    } catch (e) {
      MShopUtilF.clipboard(location.href);
      MShopUtilF.popToastMsg(T('s_about::url_copied2'), 'info');
    }
  }

  @on('click', '.hamburger_btn')
  toggleMenu() {
    if (this.toggle_open) {
      this.closeMenu();
    } else {
      this.openMenu();
    }
  }

  closeMenu() {
    const page = pipe(document.querySelector<HTMLElement>('body.handmade-creator-landing'), throwIf(isNull));
    page.classList.remove('scroll-block');

    this.toggle_open = false;
    this.dispatchEvent(toggleHeaderMenu, { bubbles: true, detail: { status: false } });
    this.redraw();
  }

  openMenu() {
    const page = pipe(document.querySelector<HTMLElement>('body.handmade-creator-landing'), throwIf(isNull));
    page.classList.add('scroll-block');

    this.toggle_open = true;
    this.dispatchEvent(toggleHeaderMenu, { bubbles: true, detail: { status: true } });
    this.redraw();
  }
}
